/**
 * AgentChat — floating AI assistant widget for the platform admin.
 *
 * Renders as a collapsed button in the bottom-right corner. Clicking it opens
 * a chat panel backed by the AssistantAgent Durable Object via
 * POST /api/ai/assistant/chat.
 */

import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, Ticket } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { assistantApi, type AssistantChatResponse } from '../../lib/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  ticketUrl?: string;
  ticketIdentifier?: string;
  error?: boolean;
}

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

export function AgentChat() {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: ChatMessage = { id: generateId(), role: 'user', text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response: AssistantChatResponse = await assistantApi.chat(
        { message: text, surface: 'platform', sessionId },
        getToken
      );

      // Persist session ID for conversation continuity
      if (response.sessionId) setSessionId(response.sessionId);

      // Extract ticket info from tool results if present
      const ticketResult = response.toolResults?.find(
        (r) => r.toolName === 'submit_ticket' && !r.error
      );
      const ticketUrl = ticketResult?.result?.url as string | undefined;
      const ticketIdentifier = ticketResult?.result?.identifier as string | undefined;

      const assistantMsg: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        text: response.message,
        ticketUrl,
        ticketIdentifier,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          text: err?.message || 'Something went wrong. Please try again.',
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        className="fixed bottom-5 right-5 z-50 flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
        style={{ background: 'linear-gradient(135deg, #d46f84 0%, #b9576d 100%)' }}
      >
        {open ? (
          <X size={20} className="text-white" />
        ) : (
          <MessageCircle size={20} className="text-white" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 flex flex-col w-80 sm:w-96 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ height: '480px', background: '#fff' }}
          role="dialog"
          aria-label="AI Assistant"
        >
          {/* Header */}
          <div
            className="flex items-center gap-2 px-4 py-3 shrink-0"
            style={{ background: 'linear-gradient(135deg, #d46f84 0%, #b9576d 100%)' }}
          >
            <Bot size={18} className="text-white" />
            <span className="text-sm font-semibold text-white">SWAK Assistant</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="ml-auto text-white/70 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center gap-2 text-gray-400">
                <Bot size={32} className="text-rose-300" />
                <p className="text-sm font-medium text-gray-600">How can I help?</p>
                <p className="text-xs text-gray-400 max-w-[220px]">
                  Ask me anything about your studio, or say "submit a ticket" to log an issue.
                </p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-rose-500 text-white rounded-br-sm'
                      : msg.error
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                  {msg.ticketUrl && (
                    <a
                      href={msg.ticketUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700 underline underline-offset-2"
                    >
                      <Ticket size={12} />
                      View {msg.ticketIdentifier || 'ticket'} in Linear
                    </a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2">
                  <Loader2 size={16} className="text-gray-400 animate-spin" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-gray-100 px-3 py-2 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message…"
              rows={1}
              disabled={loading}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:opacity-50 max-h-28 overflow-y-auto"
              style={{ lineHeight: '1.4' }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              aria-label="Send"
              className="shrink-0 flex items-center justify-center w-9 h-9 rounded-xl bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
